import { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ChatIcon } from 'basic-components/icons';

import DraggablePanel from 'components/primary/DraggablePanel';
import draggablePanelHooks from 'components/primary/DraggablePanel/Hooks';

import Content from './Content';
import Resizer from './Resizer';

import classNames from 'classnames';
import './LLMChat.scss';

const EMPTY_OBJECT = {};
const CHAT_LAYER_SELECTOR = ".ng-python-editor"
const { useDraggablePanelPosition, usePanelBoundsBySelector } = draggablePanelHooks

const LLMChat = ({ content, messages, setMessages, contentSyncToken, setContent, taskParams = EMPTY_OBJECT }) => {
  const [isExpanded, setIsExpanded] = useState(false),
        [message, setMessage] = useState(""),
        [isFetching, setIsFetching] = useState(false),
        [syncNotice, setSyncNotice] = useState(""),
        [requestVersion, setRequestVersion] = useState(0),
        hasMountedRef = useRef(false),
        messagesLengthRef = useRef(0),
        contentNodeRef = useRef(null),
        toggleExpanded = useCallback(() => setIsExpanded(expanded => !expanded), []),
        resolvePanelBounds = usePanelBoundsBySelector({ selector: CHAT_LAYER_SELECTOR }),
        { dragBounds, isDragging, onStart, onStop, panelPosition, setPanelPosition } = useDraggablePanelPosition({ panelRef: contentNodeRef, resolvePanelBounds })

  useEffect(() => {
    messagesLengthRef.current = messages.length
  }, [messages.length])

  useEffect(() => {
    if(!hasMountedRef.current) {
      hasMountedRef.current = true
      return;
    }
    setMessage("")
    if(messagesLengthRef.current > 0) {
      setMessages([])
      setSyncNotice("Editor content was reloaded. Previous chat history was cleared to keep the assistant aligned with the current file.")
      setRequestVersion(version => version + 1)
      return;
    }
    setRequestVersion(version => version + 1)
    setSyncNotice("")
  }, [contentSyncToken, setMessages])

  return (
    <div
        className={classNames("ng-python-editor__chat", { "ng-python-editor__chat--is-expanded": isExpanded })}
    >
      <button
          className="ng-python-editor__chat__toggle"
          onClick={toggleExpanded}
          type="button"
      >
        <div className="ng-python-editor__chat__toggle__icon">
          <ChatIcon/>
        </div>
      </button>
      {isExpanded ? (
        <DraggablePanel
            bounds={dragBounds}
            handle=".ng-python-editor__chat__content__header"
            isDragging={isDragging}
            nodeRef={contentNodeRef}
            onStart={onStart}
            onStop={onStop}
            position={panelPosition}
        >
          <div
              className="ng-python-editor__chat__panel"
              ref={contentNodeRef}
          >
            <Resizer
                panelPosition={panelPosition}
                panelRef={contentNodeRef}
                setPanelPosition={setPanelPosition}
            />
            <Content
                content={content}
                isFetching={isFetching}
                message={message}
                messages={messages}
                requestVersion={requestVersion}
                setContent={setContent}
                setIsFetching={setIsFetching}
                setMessage={setMessage}
                setMessages={setMessages}
                syncNotice={syncNotice}
                taskParams={taskParams}
            />
          </div>
        </DraggablePanel>
      ) : undefined}
    </div>
  )
}

LLMChat.propTypes = {
  content: PropTypes.string,
  contentSyncToken: PropTypes.number,
  messages: PropTypes.array.isRequired,
  setContent: PropTypes.func.isRequired,
  setMessages: PropTypes.func.isRequired,
  taskParams: PropTypes.object
}

export default memo(LLMChat);
